import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from 'prop-types';
import { PatternFormat } from "react-number-format";
import {
    Alert,
    Collapse,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    InputAdornment,
    Radio,
    RadioGroup,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import Input from "@/components/Tags/Input";
import axios from "@/lib/axios";
import Button from "@/components/Tags/Form/Button";
import Turnstile from "@/components/Tags/Turnstile";

const defaultValues = {
    name: '',
    mobile_no: '',
    email: '',
    desired_location: '',
    owns_lot: '',
    lot_size: '',
    employment_status: '',
    business_type: '',
    message: '',
};

const LOT_OWNERSHIP_OPTIONS = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
];

const EMPLOYMENT_STATUS_OPTIONS = [
    { value: 'employed', label: 'Employed' },
    { value: 'business_owner', label: 'Business Owner' },
    { value: 'self_employed', label: 'Self-Employed / Freelancer' },
    { value: 'retired', label: 'Retired' },
    { value: 'not_employed', label: 'Currently not employed' },
];

const NAME_MAX_LENGTH = 80;
const MESSAGE_MIN_LENGTH = 10;
const MESSAGE_MAX_LENGTH = 1000;

// Must match CAPTCHA_ACTIONS['franchise'] in api-rapideph's PageController.
const CAPTCHA_ACTION = 'franchise_inquiry';

// Reveal only — hide snaps shut, per the ticket's interaction spec.
const REVEAL_TIMEOUT = { enter: 180, exit: 0 };

const NumberFormatCustom = React.forwardRef(function NumberFormatCustom(props, ref) {
    const { onChange, ...other } = props;
    return (
        <PatternFormat
            {...other}
            getInputRef={ref}
            onValueChange={(values, sourceInfo) => {
                onChange(values.value);
            }}
            format="### ### ####"
            allowEmptyFormatting mask="-"
        />
    );
});

NumberFormatCustom.propTypes = {
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
};

// Radio group wrapped in a real fieldset/legend so screen readers announce the
// question with its options, and errors are linked via aria-describedby.
function RadioField({ id, title, options, value, onChange, error, helperText, row = false }){
    const helperTextId = `${id}-helper-text`;
    return (
        <FormControl
            component="fieldset"
            id={id}
            tabIndex={-1}
            error={error}
            className="w-full pb-2 outline-none"
        >
            <FormLabel component="legend" className="text-black/[0.6] font-normal text-sm">
                {title}
            </FormLabel>
            <RadioGroup
                name={id}
                value={value}
                onChange={onChange}
                row={row}
                aria-describedby={error ? helperTextId : undefined}
            >
                {options.map((option) => {
                    return (
                        <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={<Radio size="small" />}
                            label={option.label}
                        />
                    )
                })}
            </RadioGroup>
            {error && (
                <FormHelperText id={helperTextId}>{helperText}</FormHelperText>
            )}
        </FormControl>
    );
}

RadioField.propTypes = {
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    options: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,
};

export default function FranchiseUs(){
    const { handleSubmit, setValue, setError, clearErrors, control, watch, formState: { errors } } = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        shouldFocusError: false, // handled in onError so radio groups get focus too
        defaultValues: defaultValues
     });

    const [loading, setLoading] = useState(false);
    const [alertStatus, setAlertStatus] = useState(null);
    const [alertMessage, setAlertMessage] = useState(null);

    // Bot protection (GPH-523): invisible check, hidden trap field, and how long the form was open.
    const [captchaToken, setCaptchaToken] = useState('');
    const [captchaUnavailable, setCaptchaUnavailable] = useState(false);
    const turnstileRef = useRef(null);
    const honeypotRef = useRef(null);
    const loadedAtRef = useRef(null);
    const submittingRef = useRef(false);

    useEffect(() => {
        loadedAtRef.current = Date.now();
    }, []);

    const handleCaptchaUnavailable = useCallback(() => setCaptchaUnavailable(true), []);

    const ownsLot = watch('owns_lot');
    const employmentStatus = watch('employment_status');
    const message = watch('message');

    const showLotSize = ownsLot === 'yes';
    const showBusinessType = employmentStatus === 'business_owner';

    // Hidden conditionals must not carry stale values into the payload.
    const handleChangeOwnsLot = (value, onChange) => {
        onChange(value);
        if(value !== 'yes'){
            setValue('lot_size', '');
            clearErrors('lot_size');
        }
    };

    const handleChangeEmploymentStatus = (value, onChange) => {
        onChange(value);
        if(value !== 'business_owner'){
            setValue('business_type', '');
            clearErrors('business_type');
        }
    };

    const onSubmit = async (data) => {
        // A double click can land before the button re-renders as disabled.
        if(submittingRef.current){
            return;
        }
        // The invisible check usually finishes long before anyone can fill the form.
        // If it can't load at all, send anyway and let the server decide.
        if(!captchaToken && !captchaUnavailable){
            setAlertStatus(203);
            setAlertMessage('We\'re still verifying your browser. Please try again in a moment.');
            return;
        }

        submittingRef.current = true;
        setLoading(true);

        const payload = {
            ...data,
            captcha_token: captchaToken,
            website: honeypotRef.current?.value ?? '',
            form_elapsed_ms: Date.now() - loadedAtRef.current,
        };
        if(!showLotSize){
            delete payload.lot_size;
        }
        if(!showBusinessType){
            delete payload.business_type;
        }

        try {
            await axios.get('/sanctum/csrf-cookie');
            // The API is on another subdomain, so axios only sends the CSRF header when asked.
            const response = await axios.post(process.env.API_URL+`/franchise_us`, payload, { withXSRFToken: true });
            setAlertStatus(response.data.status);
            setAlertMessage(response.data.message);
        } catch (error) {
            const fieldErrors = Object.entries(error?.response?.data?.errors ?? {})
                                    .filter(([field]) => field in defaultValues);
            if(fieldErrors.length){
                // Show server-side rule failures on the fields themselves.
                fieldErrors.forEach(([field, messages]) => {
                    setError(field, { type: 'server', message: messages[0] });
                });
                setAlertStatus(null);
                onError(Object.fromEntries(fieldErrors));
            }
            else{
                setAlertStatus(203);
                setAlertMessage(error?.response?.data?.message ?? 'Connection Failed. Try again.');
            }
        }

        // Tokens are single-use, so fetch a fresh one for any retry.
        turnstileRef.current?.reset();
        submittingRef.current = false;
        setLoading(false);
    };

    // Scroll to and focus the first invalid field (text inputs and radio groups alike).
    const onError = (formErrors) => {
        const firstInvalid = Object.keys(formErrors)[0];
        if(!firstInvalid){
            return;
        }
        const element = document.getElementById(firstInvalid);
        if(element){
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.focus({ preventScroll: true });
        }
    };
    return(
        <div className='max-w-screen-xl mx-auto bg-white'>
            <div className='relative grid sm:grid-cols-2 grid-cols-1 sm:px-6 px-3 pt-4 pb-20'>
                <div className='px-4 pt-4'>
                    <div className='relative'>
                        <div className='absolute -bottom-4 h-1.5 w-32 bg-rapide-yellow'></div>
                        <h1 className='text-4xl'>Be <span className='font-semibold'> part of a rapidly growing P15 Billion Industry!</span></h1>
                    </div>
                    <p className='pt-12 leading-9 font-light'>
                    Your trusted auto maintenance service partner. Here at Rapidé, we are always on the lookout for our next great franchisees. Do you have what it takes to be part of a 15 billion industry?
                    </p>
                </div>
                <div className='px-4 pt-4'>
                    {alertStatus && (
                        <Alert severity={`${(alertStatus  == 200) ? 'success' : 'error'}`}>
                            {alertMessage}
                        </Alert>
                    )}
                    <form className="mt-2" onSubmit={handleSubmit(onSubmit, onError)} noValidate>
                        <Controller
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { isTouched, isDirty, error },
                            }) => {
                                return(
                                    <Input
                                        className="pb-2"
                                        type="text"
                                        id="name"
                                        name="name"
                                        label="Full Name"
                                        variant="filled"
                                        size="small"
                                        fullWidth
                                        value={value}
                                        inputRef={ref}
                                        error={errors.name != null}
                                        helperText={errors.name && errors.name?.message}
                                        onChange={(e) => {
                                            onChange(e);
                                        }}
                                        inputProps={{ maxLength: NAME_MAX_LENGTH }}
                                        InputProps={{
                                            disableUnderline: true,
                                        }}
                                    />
                                )
                            }}
                            name="name"
                            control={control}
                            rules={{
                                required: 'Please enter your full name.',
                                // First and last name, as the server requires.
                                validate: (value) => /\S\s+\S/.test(value.trim()) || 'Please enter your full name.',
                                maxLength: {
                                    value: NAME_MAX_LENGTH,
                                    message: 'Please enter your full name.'
                                },
                            }}
                        />
                        <Controller
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { isTouched, isDirty, error },
                            }) => {
                                return(
                                    <Input
                                        className="pb-2"
                                        fullWidth
                                        type="text"
                                        id="mobile_no"
                                        name="mobile_no"
                                        label="Mobile Number"
                                        variant="filled"
                                        size="small"
                                        value={value}
                                        inputRef={ref}
                                        error={errors.mobile_no != null}
                                        helperText={errors.mobile_no && errors.mobile_no?.message}
                                        onChange={(e) => {
                                            onChange(e);
                                        }}
                                        InputProps={{
                                            disableUnderline: true,
                                            startAdornment: <InputAdornment position="start">+63</InputAdornment>,
                                            inputComponent: NumberFormatCustom,
                                        }}
                                    />
                                )
                            }}
                            name="mobile_no"
                            control={control}
                            rules={{
                                required: "Enter a valid PH mobile number (10 digits).",
                                minLength: {
                                    value: 10,
                                    message: 'Enter a valid PH mobile number (10 digits).'
                                },
                                maxLength: {
                                    value: 10,
                                    message: 'Enter a valid PH mobile number (10 digits).'
                                },
                            }}
                        />
                        <Controller
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { isTouched, isDirty, error },
                            }) => {
                                return(
                                    <Input
                                        className="pb-2"
                                        fullWidth
                                        type="email"
                                        id="email"
                                        name="email"
                                        label="Email Address"
                                        variant="filled"
                                        size="small"
                                        value={value}
                                        inputRef={ref}
                                        error={errors.email != null}
                                        helperText={errors.email && errors.email?.message}
                                        onChange={(e) => {
                                            onChange(e);
                                        }}
                                        InputProps={{
                                            disableUnderline: true,
                                            startAdornment: <InputAdornment position="start"><EmailOutlinedIcon/></InputAdornment>,
                                        }}
                                    />
                                )
                            }}
                            name="email"
                            control={control}
                            rules={{
                                required: 'Enter a valid email address.',
                                pattern: {
                                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                    message: 'Enter a valid email address.'
                                },
                            }}
                        />
                        <Controller
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { isTouched, isDirty, error },
                            }) => {
                                return(
                                    <Input
                                        className="pb-2"
                                        type="text"
                                        id="desired_location"
                                        name="desired_location"
                                        label="Preferred Franchise Location"
                                        variant="filled"
                                        size="small"
                                        fullWidth
                                        value={value}
                                        inputRef={ref}
                                        error={errors.desired_location != null}
                                        helperText={errors.desired_location && errors.desired_location?.message}
                                        onChange={(e) => {
                                            onChange(e);
                                        }}
                                        inputProps={{ maxLength: 150 }}
                                        InputProps={{
                                            disableUnderline: true,
                                        }}
                                    />
                                )
                            }}
                            name="desired_location"
                            control={control}
                            rules={{
                                required: 'Please enter your preferred franchise location.',
                                validate: (value) => value.trim() !== '' || 'Please enter your preferred franchise location.',
                                maxLength: {
                                    value: 150,
                                    message: 'Please enter your preferred franchise location.'
                                },
                            }}
                        />
                        <Controller
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { isTouched, isDirty, error },
                            }) => {
                                return(
                                    <RadioField
                                        id="owns_lot"
                                        title="Do you already own a lot for the proposed franchise?"
                                        options={LOT_OWNERSHIP_OPTIONS}
                                        value={value}
                                        row
                                        onChange={(e) => {
                                            handleChangeOwnsLot(e.target.value, onChange);
                                        }}
                                        error={errors.owns_lot != null}
                                        helperText={errors.owns_lot && errors.owns_lot?.message}
                                    />
                                )
                            }}
                            name="owns_lot"
                            control={control}
                            rules={{
                                required: 'Please select Yes or No.',
                            }}
                        />
                        {/* Unmounted while hidden, so it stays out of the tab order and the a11y tree. */}
                        <Collapse in={showLotSize} timeout={REVEAL_TIMEOUT} unmountOnExit>
                            <Controller
                                render={({
                                    field: { onChange, onBlur, value, name, ref },
                                    fieldState: { isTouched, isDirty, error },
                                }) => {
                                    return(
                                        <Input
                                            className="pb-2"
                                            type="text"
                                            id="lot_size"
                                            name="lot_size"
                                            label="If YES, what is the lot size?"
                                            placeholder="e.g. 300 sqm, 500 sqm"
                                            variant="filled"
                                            size="small"
                                            fullWidth
                                            value={value}
                                            inputRef={ref}
                                            error={errors.lot_size != null}
                                            helperText={errors.lot_size && errors.lot_size?.message}
                                            onChange={(e) => {
                                                onChange(e);
                                            }}
                                            InputProps={{
                                                disableUnderline: true,
                                            }}
                                        />
                                    )
                                }}
                                name="lot_size"
                                control={control}
                                rules={{
                                    validate: (value) => !showLotSize || value.trim() !== '' || 'Please provide the lot size.',
                                }}
                            />
                        </Collapse>
                        <Controller
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { isTouched, isDirty, error },
                            }) => {
                                return(
                                    <RadioField
                                        id="employment_status"
                                        title="What is your current employment or business status?"
                                        options={EMPLOYMENT_STATUS_OPTIONS}
                                        value={value}
                                        onChange={(e) => {
                                            handleChangeEmploymentStatus(e.target.value, onChange);
                                        }}
                                        error={errors.employment_status != null}
                                        helperText={errors.employment_status && errors.employment_status?.message}
                                    />
                                )
                            }}
                            name="employment_status"
                            control={control}
                            rules={{
                                required: 'Please select your current status.',
                            }}
                        />
                        <Collapse in={showBusinessType} timeout={REVEAL_TIMEOUT} unmountOnExit>
                            <Controller
                                render={({
                                    field: { onChange, onBlur, value, name, ref },
                                    fieldState: { isTouched, isDirty, error },
                                }) => {
                                    return(
                                        <Input
                                            className="pb-2"
                                            type="text"
                                            id="business_type"
                                            name="business_type"
                                            label="If you own a business, please specify"
                                            placeholder="e.g. Restaurant, Retail, Auto Shop, Logistics"
                                            variant="filled"
                                            size="small"
                                            fullWidth
                                            value={value}
                                            inputRef={ref}
                                            onChange={(e) => {
                                                onChange(e);
                                            }}
                                            InputProps={{
                                                disableUnderline: true,
                                            }}
                                        />
                                    )
                                }}
                                name="business_type"
                                control={control}
                            />
                        </Collapse>
                        <Controller
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { isTouched, isDirty, error },
                            }) => {
                                return(
                                    <Input
                                        className="pb-2"
                                        type="text"
                                        id="message"
                                        name="message"
                                        label="Message"
                                        variant="filled"
                                        size="small"
                                        fullWidth
                                        multiline
                                        rows={4}
                                        value={value}
                                        inputRef={ref}
                                        error={errors.message != null}
                                        helperText={errors.message ? errors.message?.message : `${message.length}/${MESSAGE_MAX_LENGTH}`}
                                        onChange={(e) => {
                                            onChange(e);
                                        }}
                                        inputProps={{ maxLength: MESSAGE_MAX_LENGTH }}
                                        InputProps={{
                                            disableUnderline: true,
                                        }}
                                    />
                                )
                            }}
                            name="message"
                            control={control}
                            rules={{
                                // Optional, but if given it has to say something.
                                validate: (value) => value.trim() === '' || value.trim().length >= MESSAGE_MIN_LENGTH || 'Please enter a valid message.',
                                maxLength: {
                                    value: MESSAGE_MAX_LENGTH,
                                    message: `Message must not exceed ${MESSAGE_MAX_LENGTH} characters.`
                                },
                            }}
                        />
                        {/* Honeypot: off-screen and hidden from screen readers and the tab order. People never fill it; bots do. */}
                        <div className="absolute -left-[9999px] w-px h-px overflow-hidden" aria-hidden="true">
                            <label htmlFor="website">Website</label>
                            <input
                                ref={honeypotRef}
                                type="text"
                                id="website"
                                name="website"
                                tabIndex={-1}
                                aria-hidden="true"
                                autoComplete="off"
                                defaultValue=""
                            />
                        </div>
                        <Turnstile
                            ref={turnstileRef}
                            action={CAPTCHA_ACTION}
                            onVerify={setCaptchaToken}
                            onUnavailable={handleCaptchaUnavailable}
                        />
                        <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
                            <div className="md:col-start-2 col-start-1">
                                <Button type="submit" loading={loading} label="Submit" llabel="Submitting..." />
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}
